import { ICollection } from '@tacans/shared-marketplace-layer';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PreloadImage } from 'src/features/common/app/components/preload-image';
import { CollectionCardBig } from 'src/features/common/collection/components/collection-card-big';
import { ExploreItem } from 'src/features/common/explore/containers/nfts-explore-container/explore-item';
import { useCalculateExploreItemHeight } from 'src/features/common/explore/hooks/use-calculate-explore-item-height';
import {
    useCollectionsByPage,
    useCollectionsEntities,
    useFilters,
    usePages,
    useTotal,
    useIsSomeFilterApplied,
} from 'src/features/common/explore/hooks/use-explore-collections';
import { useInfiniteCollections } from 'src/features/common/explore/hooks/use-infinite-collections';
import { useProfile } from 'src/features/common/profile/hooks/profile.hooks';
import { EmptyListPlaceholder } from 'src/features/common/shared/components/empty-list-placeholder';
import { ItemPlaceholder } from 'src/features/common/shared/components/item-placeholder';
import { useFetchUserList } from 'src/features/common/user/hooks/user-list.hooks';
import { useDebouncedEffect } from 'src/hooks/use-debounced-effect';
import { useInfiniteScroll } from 'src/hooks/use-infinite-scroll';
import { Grid } from 'src/ui/common/atoms/grid';
import { Spacing } from 'src/ui/common/atoms/spacing';
import { Typography } from 'src/ui/common/atoms/typography';
import { useExploreComputedItems } from '../../hooks/use-explore-computed-items';
import { StyledCollection, StyledLoadMoreButton } from './styles';

const ITEMS_GRID_CONFIG = {
    xs: 1,
    sm: 2,
    md: 4,
    lg: 4,
    xl: 4,
};
const INITIAL_EXPLORE_ITEM_HEIGHT = '400px';
export const CollectionsContainer = () => {
    const router = useRouter();
    const {
        query: { login },
    } = router;
    const { t } = useTranslation(['actions', 'components']);
    const wrapperRef = useRef() as React.MutableRefObject<HTMLDivElement>;
    const filters = useFilters();
    const isFilterApplied = useIsSomeFilterApplied();
    const fetchUserList = useFetchUserList();
    const profile = useProfile();

    const [infinityScrollEnabled, setInfinityScrollEnabled] = useState(false);

    const { itemHeight, calculateItemHeight } = useCalculateExploreItemHeight({
        selector: 'data-test-id=infinite-collections-item',
        initialHeight: INITIAL_EXPLORE_ITEM_HEIGHT,
    });

    const { fetch, loading, total, pager } = useInfiniteCollections({ limit: 20 });

    const items = useExploreComputedItems<ICollection>({
        limit: 20,
        useEntities: useCollectionsEntities,
        useByPage: useCollectionsByPage,
        useTotal,
        usePages,
    });

    const visibleItems = useMemo(() => {
        const visibleCount = 12;

        if (infinityScrollEnabled || total < visibleCount || pager?.page) {
            return items;
        }
        return items.slice(0, visibleCount);
    }, [infinityScrollEnabled, items, total, pager]);

    const loadMore = useCallback(() => {
        if (!loading) {
            fetch({ page: pager.page! + 1 });
        }
    }, [pager.page, loading, fetch]);

    const onFiltersChange = useCallback(() => {
        fetch({ page: 0 });
    }, [fetch]);

    const isPrivate = !!profile && login === profile?.user?.login;
    const ownValue = useMemo(() => {
        if (isFilterApplied || !login) {
            return '';
        }
        return isPrivate ? '.private' : '.public';
    }, [login, isPrivate, isFilterApplied]);

    const emptyList = useMemo(
        () => ({
            title: t(`emptyList.collection${ownValue}.title`, { ns: 'components' }),
            description: t(`emptyList.collection.${isFilterApplied ? 'textSearch' : 'text'}`, { ns: 'components' }),
        }),
        [t, ownValue, isFilterApplied]
    );

    useInfiniteScroll({
        cb: loadMore,
        disabled: !infinityScrollEnabled,
        threshold: 300,
    });

    useEffect(() => {
        calculateItemHeight();
    }, [calculateItemHeight, total, items]);

    useEffect(() => {
        const mutationObserver = new MutationObserver(calculateItemHeight);
        const resizeObserver = new ResizeObserver(calculateItemHeight);
        if (wrapperRef.current) {
            resizeObserver.observe(wrapperRef.current);
            mutationObserver.observe(wrapperRef.current, {
                childList: true,
                subtree: true,
            });
        }
        return () => {
            if (wrapperRef.current) {
                mutationObserver.disconnect();
                resizeObserver.unobserve(wrapperRef.current);
            }
        };
    }, [calculateItemHeight]);

    useDebouncedEffect(onFiltersChange, 50, [filters]);

    useEffect(() => {
        fetchUserList({
            userIds: visibleItems.map((item) => item?.ownerId!),
        });
    }, [fetchUserList, visibleItems]);

    return (
        <div ref={wrapperRef}>
            {visibleItems[0] && <PreloadImage src={visibleItems[0]?.previewAssets?.webps?.mediumUrl!} />}
            {!loading && !+total && (
                <EmptyListPlaceholder itemsGridConfig={ITEMS_GRID_CONFIG}>
                    <Typography size="base" tag="div" variant="title" color="text">
                        {emptyList.title}
                    </Typography>
                    <Spacing margin="10px 0 30px">
                        <Typography size="sm" tag="div" color="gs200">
                            <span dangerouslySetInnerHTML={{ __html: emptyList.description }} />
                        </Typography>
                    </Spacing>
                </EmptyListPlaceholder>
            )}
            <Grid
                {...ITEMS_GRID_CONFIG}
                columnGap={10}
                columnGapMd={16}
                columnGapLg={16}
                rowGap={10}
                rowGapSm={20}
                rowGapMd={40}
            >
                {visibleItems.map((item, index) => (
                    <ExploreItem height={itemHeight} key={item?.id ?? index}>
                        {item ? (
                            <StyledCollection>
                                <CollectionCardBig
                                    collection={item}
                                    key={item?.id ?? index}
                                    testId="infinite-collections-item"
                                />
                            </StyledCollection>
                        ) : (
                            <ItemPlaceholder />
                        )}
                    </ExploreItem>
                ))}
            </Grid>
            {!infinityScrollEnabled && total > visibleItems.length && (
                <StyledLoadMoreButton
                    stretch
                    disabled={loading}
                    onClick={() => setInfinityScrollEnabled(true)}
                    variant="grey"
                >
                    {t('misc.loadMore', { ns: 'actions' })}
                </StyledLoadMoreButton>
            )}
        </div>
    );
};
